import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import FloridaEstimator from "@/components/FloridaEstimator";
import { buildSchema, safeJsonLd } from "@/lib/florida-schema";
import { FLORIDA_HUB, floridaLabel } from "@/lib/florida-nav";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";

const BUSINESS = {
  brand: "Commercial Solar ROI",
  description: "Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform, providing project assessments, financial calculators, utility-bill reviews, and solar-plus-storage planning. Lawrence Williams leads commercial sales.",
  canonicalOrigin: "https://commercialsolarroi.com",
  publicPhone: "+1-310-881-5155",
  sameAs: [],
  physicalLocations: [], // verified eligible public locations only — none claimed
};

export default function FloridaPage() {
  const { pathname } = useLocation();
  const { config, openAudit } = useSite();
  const seg = pathname.toLowerCase().replace(/\/+$/, "").split("/florida-commercial-solar")[1] || "";
  const path = "/florida-commercial-solar" + seg;
  const [page, setPage] = useState(null);
  const [state, setState] = useState("loading");
  const [sources, setSources] = useState([]);

  useEffect(() => {
    let alive = true;
    setState("loading");
    setPage(null);
    api.get("/florida/pages", { params: { path } })
      .then((r) => { if (alive) { setPage(r.data); setState("ready"); } })
      .catch(() => { if (alive) setState("missing"); });
    return () => { alive = false; };
  }, [path]);

  useEffect(() => {
    api.get("/florida/sources").then((r) => setSources(r.data.sources)).catch(() => {});
  }, []);

  useEffect(() => {
    if (state !== "ready" || !page) return undefined;
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.id = "fl-jsonld";
    node.text = safeJsonLd(buildSchema(page, BUSINESS));
    document.head.appendChild(node);
    return () => { document.getElementById("fl-jsonld")?.remove(); };
  }, [state, page]);

  if (state === "loading") return null;
  if (state === "missing") return <NotFound />;
  const related = page.relatedPaths || [];
  const pageSources = sources.filter((s) => (page.sourceIds || []).includes(s.id));

  return (
    <>
      <Seo title={page.title} description={page.description} />
      <section className="page-hero">
        <div className="wrap">
          <p className="micro" style={{ marginBottom: 14 }}>
            <Link to="/">Home</Link> · <Link to={FLORIDA_HUB}>Florida Commercial Solar</Link>{page.path !== FLORIDA_HUB ? ` · ${page.h1}` : ""}
          </p>
          <span className="eyebrow">FLORIDA COMMERCIAL SOLAR · {page.kind.toUpperCase()}</span>
          <h1 data-testid="fl-heading">{page.h1}</h1>
          <p className="lead">{page.intro}</p>
          <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
            <button className="btn lime" data-testid="fl-upload-cta" onClick={() => openAudit({ originPath: page.path, utility: page.utilityId || null })}>
              {page.cta || "Get My Bill-Based Solar ROI Review"} <span>↗</span>
            </button>
            <a className="btn light" href={config.phoneHref}>Call {config.phone}</a>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 40 }}>
        <div className="wrap city-grid">
          <div>
            {page.sections.map((s, i) => (
              <Reveal key={s.heading} delay={Math.min(i * 0.04, 0.16)}>
                <section style={{ marginBottom: 24 }}>
                  <h2 style={{ fontSize: 24, margin: "0 0 10px" }}>{s.heading}</h2>
                  <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.75, maxWidth: 660, whiteSpace: "pre-line" }}>{s.body}</p>
                </section>
              </Reveal>
            ))}
            {page.calculator && (
              <Reveal delay={0.06}>
                <div id="florida-roi" className="sbb-engine" style={{ padding: "26px 22px" }} data-testid="fl-estimator-mount">
                  <FloridaEstimator initialUtility={page.utilityId || "FPL"} sourceCheckDate={page.reviewedOn} />
                </div>
              </Reveal>
            )}
            {page.faqs?.length > 0 && (
              <Reveal delay={0.06}>
                <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Questions about this review</h2>
                <div data-testid="fl-faq">
                  {page.faqs.map((f) => (
                    <details key={f.question}><summary>{f.question}</summary><p>{f.answer}</p></details>
                  ))}
                </div>
              </Reveal>
            )}
            {related.length > 0 && (
              <Reveal delay={0.06}>
                <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Continue your review</h2>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="fl-related">
                  {related.map((p) => (
                    <Link key={p} className="btn light" to={p.replace(/\/+$/, "") || p}>{floridaLabel(p)}</Link>
                  ))}
                </div>
              </Reveal>
            )}
          </div>
          <Reveal delay={0.1}>
            <aside className="report-preview" style={{ position: "sticky", top: 90 }} data-testid="fl-sources">
              <span className="eyebrow">OFFICIAL RESOURCES</span>
              <ul className="benefitlist" style={{ marginTop: 10 }}>
                {pageSources.map((s) => (
                  <li key={s.id}>
                    <a href={s.url} target="_blank" rel="noreferrer">{s.title} ↗</a>
                    <br />
                    <small className="muted">{s.status}</small>
                  </li>
                ))}
                {!pageSources.length && <li>Official sources for this page are listed in the site's source registry.</li>}
              </ul>
              <p className="micro" style={{ marginTop: 12 }}>Source check: {page.reviewedOn}. Account terms and current law control. Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform — Coastal Energy performs the project services; Lawrence Williams leads commercial sales. No invented offices, reviews or tax promises.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
                <button className="btn" onClick={() => openAudit({ originPath: page.path })}>Upload my electric bill <span>↗</span></button>
                <Link className="btn light" to="/gallery">See real installations ↗</Link>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
      <FinalCta title={["Let's talk", "Florida."]} service="commercial" context="Florida hub quote request" />
    </>
  );
}
